import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../config/api';
import Footer from '../../components/Footer';
import Onboarding from '../../components/Onboarding';
import logo from '../../assets/logo.png';
import { canStartGuestSession, recordGuestSession, getRemainingGuestSessions, getGuestResetTime, MAX_SESSIONS } from '../../utils/guestSessions';
import { getNudgeSettings, saveNudgeSettings, requestNotificationPermission, getNotificationPermission, initNudges } from '../../utils/hungerNudge';
import { getStreak, getDailyChallenge, DAILY_SWIPE_GOAL } from '../../utils/streaks';
import { getReferralCode, shareReferral, getReferralStats } from '../../utils/referrals';
import './HomeScreen.css';

export default function HomeScreen() {
  const { user, refreshUser, hasSession } = useAuth();
  const isGuest = !user && !hasSession;
  const navigate = useNavigate();
  const [recentPicks, setRecentPicks] = useState([]);
  const [pendingSession, setPendingSession] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem('hangrily_onboarded')
  );
  const [nudgeSettings, setNudgeSettings] = useState(getNudgeSettings);
  const [streakData, setStreakData] = useState({ current: 0, best: 0, todayDone: false });
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinCode, setJoinCode] = useState('');
  const [joinModalMode, setJoinModalMode] = useState('group');

  const loadRecentPicks = useCallback(async () => {
    if (isGuest) return;
    try {
      const { data } = await api.get('/analytics/history?limit=3');
      setRecentPicks(data.history || []);
    } catch (err) {
      console.error('Failed to load recent picks:', err);
    }
  }, [isGuest]);

  const checkPendingSession = useCallback(async () => {
    if (isGuest) return;
    try {
      const { data } = await api.get('/sessions/pending');
      if (data.session) {
        setPendingSession(data.session);
      }
    } catch (err) {
      // No pending sessions
    }
  }, [isGuest]);

  useEffect(() => {
    loadRecentPicks();
    checkPendingSession();
    if (!isGuest) refreshUser();
    initNudges();
    setStreakData(getStreak());
  }, [loadRecentPicks, checkPendingSession, refreshUser, isGuest]);

  function getGreeting() {
    const hour = new Date().getHours();
    if (hour < 11) return 'Good morning';
    if (hour < 15) return 'Lunchtime';
    if (hour < 18) return 'Good afternoon';
    return 'Dinner time';
  }

  function startGame(mode) {
    navigate('/preferences', { state: { mode } });
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  }

  const stats = user?.stats || {};
  const totalSwipes = stats.totalSwipes || 0;
  const totalDecisions = stats.totalDecisions || 0;
  const firstName = user?.firstName || 'there';

  async function toggleNudges() {
    if (!nudgeSettings.enabled) {
      const perm = await requestNotificationPermission();
      if (perm === 'granted') {
        const updated = { ...nudgeSettings, enabled: true };
        setNudgeSettings(updated);
        saveNudgeSettings(updated);
      } else if (perm === 'denied') {
        alert('Notifications are blocked. Please enable them in your browser settings.');
      }
    } else {
      const updated = { ...nudgeSettings, enabled: false };
      setNudgeSettings(updated);
      saveNudgeSettings(updated);
    }
  }

  const dailyChallenge = getDailyChallenge();
  const referralCode = user?.id ? getReferralCode(user.id) : null;
  const referralStats = getReferralStats(user?.stats);

  function handleModeClick(mode) {
    if (isGuest && !canStartGuestSession()) {
      navigate('/try');
      return;
    }
    if (isGuest) {
      recordGuestSession();
    }
    startGame(mode);
  }

  function handleJoinSession() {
    const code = joinCode.trim().toUpperCase();
    if (!code || code.length !== 6) {
      alert('Please enter a valid 6-digit session code');
      return;
    }
    setShowJoinModal(false);
    setJoinCode('');
    navigate('/preferences', { state: { mode: joinModalMode, joinSessionCode: code } });
  }

  function openJoinModal(mode) {
    setJoinModalMode(mode);
    setShowJoinModal(true);
  }

  return (
    <div className="dash">
      {showOnboarding && <Onboarding onComplete={() => setShowOnboarding(false)} />}

      {/* Top Bar */}
      <header className="dash-topbar">
        <img src={logo} alt="Hangrily" className="dash-logo" />
        <div className="dash-topbar-right">
          {isGuest ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px', padding: '6px 14px', fontSize: '14px', cursor: 'pointer' }} onClick={() => navigate('/login')}>Log In</button>
              <button className="btn btn-sm" style={{ background: '#FF6347', color: '#fff', border: 'none', borderRadius: '8px', padding: '6px 14px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }} onClick={() => navigate('/register')}>Sign Up</button>
            </div>
          ) : (
            <>
              <button className="dash-avatar-btn" onClick={() => setMenuOpen(!menuOpen)}>
                <span className="dash-avatar-letter">{firstName.charAt(0).toUpperCase()}</span>
              </button>
              {menuOpen && (
                <>
                  <div className="dash-menu-overlay" onClick={() => setMenuOpen(false)} />
                  <div className="dash-dropdown">
                    <button onClick={() => { setMenuOpen(false); navigate('/profile'); }}>
                      <span className="dash-dd-icon">👤</span> Profile
                    </button>
                    <button onClick={() => { setMenuOpen(false); navigate('/subscription'); }}>
                      <span className="dash-dd-icon">💎</span> Subscription
                    </button>
                    <button onClick={() => { setMenuOpen(false); navigate('/analytics'); }}>
                      <span className="dash-dd-icon">📊</span> Analytics
                    </button>
                    <button onClick={() => { setMenuOpen(false); navigate('/couple-link'); }}>
                      <span className="dash-dd-icon">💑</span> Partner Link
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <main className="dash-main">
        <div className="dash-container">
          {/* Greeting */}
          <div className="dash-greeting">
            <h1>{getGreeting()}{isGuest ? '!' : `, ${firstName}!`}</h1>
            <p>{isGuest ? 'Try it out — pick a restaurant in under 2 minutes.' : 'What are you hungry for?'}</p>
          </div>

          {/* Pending Session Banner */}
          {pendingSession && (
            <button
              className="dash-invite"
              onClick={() => navigate('/preferences', { state: { mode: 'couple', joinSessionCode: pendingSession.sessionCode || pendingSession.code } })}
            >
              <span className="dash-invite-pulse" />
              <span className="dash-invite-emoji">💕</span>
              <span className="dash-invite-text">
                <strong>Partner waiting!</strong>
                <small>{pendingSession.creatorName || user?.partnerName || 'Your partner'} started a session</small>
              </span>
              <span className="dash-invite-arrow">→</span>
            </button>
          )}

          {/* Mode Selector — primary CTA */}
          <section className="dash-modes">
            <div className="dash-mode dash-mode--smart" onClick={() => navigate('/smart-search')} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <span className="dash-mode-emoji">🤖</span>
              <div className="dash-mode-info">
                <h3>Smart Search</h3>
                <p>Tell us what you want in plain English and AI finds the perfect match. "I want spicy tacos under $15"</p>
              </div>
              <span className="dash-mode-go" style={{ color: 'white' }}>Try it →</span>
            </div>

            <div className="dash-mode dash-mode--solo" onClick={() => handleModeClick('solo')}>
              <span className="dash-mode-emoji">🍽️</span>
              <div className="dash-mode-info">
                <h3>Swipe Search - Solo</h3>
                <p>Swipe through nearby restaurants and let the app pick your perfect meal. No more scrolling through apps.</p>
              </div>
              <span className="dash-mode-go">Start →</span>
            </div>

            <div className="dash-mode dash-mode--couple">
              <span className="dash-mode-emoji">💑</span>
              <div className="dash-mode-info">
                <h3>Swipe Search - Couple</h3>
                <p>{!isGuest && user?.partner ? `Swipe with ${user.partnerName} in real time — only restaurants you both like make the cut.` : 'Share a code with your partner — both swipe, and you match on restaurants you both love.'}</p>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button
                  className="btn btn-primary"
                  style={{ flex: 1, padding: '10px 16px', fontSize: '14px', fontWeight: '600' }}
                  onClick={() => handleModeClick('couple')}
                >
                  Create Session
                </button>
                <button
                  className="btn btn-secondary"
                  style={{ flex: 1, padding: '10px 16px', fontSize: '14px', fontWeight: '600' }}
                  onClick={(e) => { e.stopPropagation(); openJoinModal('couple'); }}
                >
                  Join Session
                </button>
              </div>
            </div>

            <div className="dash-mode dash-mode--group">
              <span className="dash-mode-emoji">👥</span>
              <div className="dash-mode-info">
                <h3>Swipe Search - Group</h3>
                <p>Share a code with friends or family — everyone swipes, and the group's top pick wins.</p>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1, padding: '10px 16px', fontSize: '14px', fontWeight: '600' }}
                  onClick={() => handleModeClick('group')}
                >
                  Create Session
                </button>
                <button 
                  className="btn btn-secondary" 
                  style={{ flex: 1, padding: '10px 16px', fontSize: '14px', fontWeight: '600' }}
                  onClick={(e) => { e.stopPropagation(); openJoinModal('group'); }}
                >
                  Join Session
                </button>
              </div>
            </div>
          </section>

          {/* How It Works */}
          <section className="dash-how">
            <h2 className="dash-card-title">How It Works</h2>
            <div className="dash-steps">
              <div className="dash-step">
                <span className="dash-step-num">1</span>
                <div>
                  <strong>Choose a mode</strong>
                  <p>Solo, Couple, or Group — pick how you want to decide.</p>
                </div>
              </div>
              <div className="dash-step">
                <span className="dash-step-num">2</span>
                <div>
                  <strong>Set your preferences</strong>
                  <p>Pick cuisines, distance, and price range to narrow it down.</p>
                </div>
              </div>
              <div className="dash-step">
                <span className="dash-step-num">3</span>
                <div>
                  <strong>Swipe on restaurants</strong>
                  <p>Right for yes, left for no — just like dating apps, but for food.</p>
                </div>
              </div>
              <div className="dash-step">
                <span className="dash-step-num">4</span>
                <div>
                  <strong>Get your match</strong>
                  <p>We pick the top restaurant based on your swipes. Bon appétit!</p>
                </div>
              </div>
            </div>
          </section>

          {/* Stats Row */}
          <section className="dash-stats">
            <div className="dash-stat">
              <span className="dash-stat-val">{totalSwipes}</span>
              <span className="dash-stat-lbl">Swipes</span>
            </div>
            <div className="dash-stat-divider" />
            <div className="dash-stat">
              <span className="dash-stat-val">{totalDecisions}</span>
              <span className="dash-stat-lbl">Decisions</span>
            </div>
            <div className="dash-stat-divider" />
            <div className="dash-stat">
              <span className="dash-stat-val">{stats.favoriteRestaurant ? '🏆' : '—'}</span>
              <span className="dash-stat-lbl">Top Spot</span>
            </div>
          </section>

          {/* Premium Upsell — visible, not buried in a menu */}
          {!isGuest && !user?.isPremium && (
            <section className="dash-premium-cta" onClick={() => navigate('/subscription')}>
              <span className="premium-cta-emoji">💎</span>
              <div className="premium-cta-info">
                <strong>Go Premium — $4.99/mo</strong>
                <small>Unlimited swipes, $1 cashback per order, Couple &amp; Group modes</small>
              </div>
              <span className="premium-cta-arrow">→</span>
            </section>
          )}

          {/* Streak & Daily Challenge */}
          {!isGuest && (
            <section className="dash-streak">
              <div className="dash-streak-top">
                <div className="dash-streak-fire">
                  <span className="streak-emoji">{streakData.current > 0 ? '🔥' : '💤'}</span>
                  <div>
                    <span className="streak-count">{streakData.current}</span>
                    <span className="streak-label">day streak</span>
                  </div>
                </div>
                <div className="dash-streak-progress">
                  <div className="streak-progress-bar">
                    <div className="streak-progress-fill" style={{ width: `${Math.min(100, (streakData.todaySwipes / DAILY_SWIPE_GOAL) * 100)}%` }} />
                  </div>
                  <span className="streak-progress-text">
                    {streakData.todayDone ? '✅ Daily goal done!' : `${streakData.todaySwipes}/${DAILY_SWIPE_GOAL} swipes today`}
                  </span>
                </div>
              </div>
              <div className="dash-challenge">
                <span className="challenge-emoji">{dailyChallenge.emoji}</span>
                <div className="challenge-info">
                  <span className="challenge-label">Daily Challenge</span>
                  <span className="challenge-text">{dailyChallenge.text}</span>
                </div>
                <span className="challenge-bonus">+{dailyChallenge.bonus} pts</span>
              </div>
            </section>
          )}

          {/* Hunger Nudge Toggle */}
          {!isGuest && (
            <button className={`dash-nudge-toggle ${nudgeSettings.enabled ? 'active' : ''}`} onClick={toggleNudges}>
              <span className="nudge-icon">{nudgeSettings.enabled ? '🔔' : '🔕'}</span>
              <div className="nudge-info">
                <strong>Hunger Nudge</strong>
                <small>{nudgeSettings.enabled ? 'You\'ll get reminders at lunch & dinner' : 'Get meal-time reminders to decide faster'}</small>
              </div>
              <span className={`nudge-status ${nudgeSettings.enabled ? 'on' : ''}`}>{nudgeSettings.enabled ? 'ON' : 'OFF'}</span>
            </button>
          )}

          {/* Referral Section */}
          {!isGuest && referralCode && (
            <section className="dash-referral">
              <div className="referral-header">
                <span className="referral-emoji">🎁</span>
                <div>
                  <h3 className="referral-title">Invite Friends, Get Premium Free</h3>
                  <p className="referral-desc">You both get a free week of Premium for each friend who signs up</p>
                </div>
              </div>
              <div className="referral-code-row">
                <span className="referral-code">{referralCode}</span>
                <button className="referral-share-btn" onClick={() => {
                  const result = shareReferral(user.id);
                  if (result === 'copied') alert('Referral link copied!');
                }}>
                  📤 Share
                </button>
              </div>
              {referralStats.totalReferred > 0 && (
                <div className="referral-stats">
                  <span>{referralStats.totalReferred} friend{referralStats.totalReferred !== 1 ? 's' : ''} referred</span>
                  <span className="referral-dot">&middot;</span>
                  <span>{referralStats.premiumWeeksEarned} free week{referralStats.premiumWeeksEarned !== 1 ? 's' : ''} earned</span>
                </div>
              )}
            </section>
          )}

          {/* Guest Sign-up CTA */}
          {isGuest && (
            <button className="dash-partner-cta" onClick={() => navigate('/register')} style={{ background: 'linear-gradient(135deg, #FF6347, #FF8C69)' }}>
              <span>🚀</span>
              <span>Create a free account to unlock all features</span>
              <span className="dash-partner-arrow">→</span>
            </button>
          )}

          {/* Partner Link Prompt */}
          {!isGuest && !user?.partner && (
            <button className="dash-partner-cta" onClick={() => navigate('/couple-link')}>
              <span>💕</span>
              <span>Link your partner for couple mode</span>
              <span className="dash-partner-arrow">→</span>
            </button>
          )}

          {/* Two-column grid: Recent Picks + Quick Links (hide for guests) */}
          {!isGuest && <div className="dash-grid">
            {/* Recent Picks */}
            <section className="dash-card dash-card--picks">
              <h2 className="dash-card-title">Recent Picks</h2>
              {recentPicks.length > 0 ? (
                <div className="dash-picks">
                  {recentPicks.map((d) => (
                    <div key={d.id} className="dash-pick">
                      <span className="dash-pick-emoji">
                        {d.mode === 'solo' ? '🍽️' : d.mode === 'couple' ? '💑' : '👥'}
                      </span>
                      <div className="dash-pick-info">
                        <strong>{d.restaurant || d.finalRestaurant?.name || 'Unknown'}</strong>
                        <small>{d.cuisine || d.finalRestaurant?.cuisine || 'Restaurant'} · {d.mode}</small>
                      </div>
                      <span className="dash-pick-date">{formatDate(d.decisionDate || d.decision_date)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="dash-empty">
                  <span className="dash-empty-emoji">🍕</span>
                  <p>No picks yet — start a session!</p>
                </div>
              )}
            </section>

            {/* Quick Links */}
            <section className="dash-card dash-card--links">
              <h2 className="dash-card-title">Quick Links</h2>
              <nav className="dash-links">
                <button onClick={() => navigate('/favorites')} className="dash-link">
                  <span className="dash-link-icon">❤️</span>
                  <span>Favorites</span>
                </button>
                <button onClick={() => navigate('/lists')} className="dash-link">
                  <span className="dash-link-icon">📋</span>
                  <span>My Lists</span>
                </button>
                <button onClick={() => navigate('/analytics')} className="dash-link">
                  <span className="dash-link-icon">📊</span>
                  <span>Analytics</span>
                </button>
                <button onClick={() => navigate('/rewards')} className="dash-link">
                  <span className="dash-link-icon">🎯</span>
                  <span>Rewards</span>
                </button>
                <button onClick={() => navigate('/invite')} className="dash-link">
                  <span className="dash-link-icon">🎁</span>
                  <span>Invite & Earn</span>
                </button>
                <button onClick={() => navigate('/subscription')} className="dash-link">
                  <span className="dash-link-icon">💎</span>
                  <span>Premium</span>
                </button>
              </nav>
            </section>
          </div>}
        </div>
      </main>

      <Footer />

      {/* Join Session Modal */}
      {showJoinModal && (
        <>
          <div className="modal-overlay" onClick={() => setShowJoinModal(false)} />
          <div className="modal-content" style={{ maxWidth: '400px' }}>
            <h2 style={{ marginBottom: '16px', fontSize: '24px', fontWeight: '700' }}>
              {joinModalMode === 'couple' ? 'Join Couple Session' : 'Join Group Session'}
            </h2>
            <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
              Enter the 6-digit code shared by your {joinModalMode === 'couple' ? 'partner' : 'session creator'}
            </p>
            <input
              type="text"
              placeholder="ABC123"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
              maxLength={6}
              style={{
                width: '100%',
                padding: '14px',
                fontSize: '18px',
                fontWeight: '600',
                textAlign: 'center',
                letterSpacing: '4px',
                border: '2px solid #ddd',
                borderRadius: '12px',
                marginBottom: '20px',
                textTransform: 'uppercase'
              }}
              onKeyPress={(e) => e.key === 'Enter' && handleJoinSession()}
              autoFocus
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn-secondary"
                style={{ flex: 1, padding: '12px' }}
                onClick={() => { setShowJoinModal(false); setJoinCode(''); }}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ flex: 1, padding: '12px' }}
                onClick={handleJoinSession}
                disabled={joinCode.trim().length !== 6}
              >
                Join Session
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
